// Shared helpers for the tiered Style/Label/Method grade assignment
// ({value,grades,bagLabels} — bagLabels is style-tier only, null elsewhere).
// Consumed by TieredGradeList (the add default-rule) and the Form/Manage pages
// (payload build, hydration, dirty-compare canonicalization, prune-on-grade-change).

const entryValue = (e) => (e && typeof e === 'object' ? e.value : e);

// Per-style bag-label selection, wire form: null = Default · 'NONE' = cleared ·
// [ints] = pins ([] normalizes to 'NONE' per Contract C2). Label/Method entries
// carry null — the server ignores bagLabels outside the styles tier.
const bagWire = (v) => {
  if (v === 'NONE') return 'NONE';
  if (Array.isArray(v)) return v.length ? v : 'NONE';
  return null;
};

// Default grades for a fresh entry by G (= spec grade count):
//   G=0 -> [] (None); G=1 -> [single]; G>=2 -> ALL (the whole set).
export function defaultGradesForG(specGrades) {
  const arr = Array.isArray(specGrades) ? specGrades : [];
  if (arr.length === 0) return [];
  if (arr.length === 1) return [arr[0]];
  return [...arr];
}

// POST/PUT payload for a tier: always [{value,grades}]. The server's
// normalizeTieredInput accepts this and re-coerces; normalizeWriteAssignment
// collapses covers-all/empty -> NULL and force-NULLs NO_PLU, so raw grades are fine.
// coerce = Number for Style/Method, String for Label. Number tiers drop NaN
// (matching the legacy `.map(Number).filter(!NaN)`); the String tier drops nothing.
export function buildTiered(entries, coerce) {
  return (entries || [])
    .map((e) => ({
      value: coerce(entryValue(e)),
      grades: e && typeof e === 'object' && Array.isArray(e.grades) ? e.grades : [],
      // PUT resets a style to Default when its entry lacks bagLabels, so every
      // entry must carry it — absent -> null (Default) — or Manage saves wipe pins (R1).
      bagLabels: bagWire(e && typeof e === 'object' ? e.bagLabels : null),
    }))
    .filter((e) => !(typeof e.value === 'number' && Number.isNaN(e.value)));
}

// Hydrate a tier from the server's resolved *Assignments ([{value,grades}]),
// falling back to legacy scalar-id arrays (grades default []). Grades are already
// resolved server-side — NEVER re-default on hydrate (that would phantom-dirty).
// coerce = Number for Style/Method, String for Label.
export function hydrateTiered(assignments, legacyIds, coerce) {
  if (Array.isArray(assignments)) {
    return assignments.map((a) => ({
      value: coerce(entryValue(a)),
      grades: a && Array.isArray(a.grades) ? a.grades : [],
      bagLabels: bagWire(a ? a.bagLabels : null),
    }));
  }
  return (legacyIds || []).map((v) => ({ value: coerce(entryValue(v)), grades: [], bagLabels: null }));
}

// Canonicalize tiered entries for dirty-compare: collapse ALL/None to one sentinel
// (mirrors the server's NULL collapse) and make grade order irrelevant, while
// PRESERVING outer order (it drives Position, so a row reorder is a real edit).
export function canonTieredEntries(arr, specGrades) {
  const all = [...new Set(Array.isArray(specGrades) ? specGrades : [])];
  return (arr || []).map((e) => {
    const g = e && typeof e === 'object' && Array.isArray(e.grades) ? e.grades : [];
    const kept = all.filter((x) => g.includes(x));
    const collapsed = (kept.length === 0 || kept.length === all.length) ? '*' : kept.slice().sort().join('|');
    // Bag-label token: 'D' (Default) / 'N' (cleared) / sorted pin ids — so pin edits
    // dirty the form while pin ORDER (non-semantic, R7) and Label/Method tiers don't.
    const bag = bagWire(e && typeof e === 'object' ? e.bagLabels : null);
    const b = bag === null ? 'D' : (bag === 'NONE' ? 'N' : bag.map(Number).sort((x, y) => x - y).join(','));
    return { value: entryValue(e), g: collapsed, b };
  });
}

// Re-intersect each entry's grades when the spec grade set changes. An entry that
// WAS ALL (held every grade in the OLD set) widens to the NEW full set; a subset is
// intersected with the new set; if that would empty while grades still exist it
// widens to ALL (None is only valid at G=0, per the >=1 rule). The __NO_PLU__ label
// row re-tiers like any other entry — it is no longer force-emptied.
export function reTier(entries, oldSpecGrades, newSpecGrades) {
  const oldAll = [...new Set(Array.isArray(oldSpecGrades) ? oldSpecGrades : [])];
  const newAll = [...new Set(Array.isArray(newSpecGrades) ? newSpecGrades : [])];
  return (entries || []).map((e) => {
    if (!e || typeof e !== 'object') return e;
    const cur = Array.isArray(e.grades) ? e.grades : [];
    const wasAll = oldAll.length > 0 && oldAll.every((g) => cur.includes(g));
    if (wasAll) return { ...e, grades: [...newAll] };
    const kept = newAll.filter((g) => cur.includes(g));
    if (kept.length === 0 && newAll.length > 0) return { ...e, grades: [...newAll] }; // never None at G>=1
    return { ...e, grades: kept };
  });
}
